import * as m from '@/paraglide/messages';
import { cn } from '@listening/utils/react-helpers';
import { delayMs } from '@listening/utils/ts-utils';
import ReferIcon from '@listening/assets/icons/refer.svg?react';

import { DialogContent } from '@listening/frontend/components/radix/dialog';
import { Input } from '@listening/frontend/components/radix/input';
import { Label } from '@listening/frontend/components/radix/label';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@listening/frontend/components/radix/tooltip';
import { TooltipButton } from '@listening/frontend/components/custom/tooltip-button';
import { Check, Copy } from 'iconoir-react';
import { useState } from 'react';
import { useAnalytics } from '@listening/frontend/analytics/client';
import { translucentDialogSurfaceClassName } from './dialog-surface';

export function ForwardEmailDialog() {
  const { capture } = useAnalytics();
  return (
    <DialogContent
      className={cn('w-full max-w-[480px]', translucentDialogSurfaceClassName)}
    >
      <div className='flex flex-col gap-5'>
        <p className='text-xs font-medium tracking-wider text-[var(--v2-text-muted)] uppercase'>
          {m.dialog_forward_email()}
        </p>

        <p className='text-sm text-[var(--v2-text-muted)]'>
          {m.dialog_forward_email_desc()}
        </p>

        <CopyTextInputAndButton
          text={m.dialog_forward_email_address()}
          onCopy={() => {
            capture('upload_audio_ui_interaction', {
              interaction: 'forward_email_copied',
              location: 'forward_email_dialog',
            });
          }}
          label={m.dialog_forward_email_label()}
        />
      </div>
    </DialogContent>
  );
}

ForwardEmailDialog.Icon = ({ className }: { className?: string }) => (
  <div className='rounded-full bg-rose-50/50 p-[4.5px] dark:bg-white/5'>
    <div className='rounded-full bg-rose-50 p-3 dark:bg-white/5'>
      <ReferIcon className={cn('size-8 text-rose-720', className)} />
    </div>
  </div>
);

export function CopyTextInputAndButton({
  text,
  onCopy,
  label,
}: {
  text: string;
  onCopy?: () => void;
  label?: string;
}) {
  const [showCopied, setShowCopied] = useState(false);

  return (
    <div className='space-y-2'>
      {label && (
        <Label
          htmlFor='forward-email'
          className='text-xs font-medium text-[var(--v2-text-muted)]'
        >
          {label}
        </Label>
      )}
      <div className='flex items-center gap-2'>
        <Input
          className='h-12 flex-1 rounded-xl border-[length:var(--v2-border-width)] border-[var(--v2-card-border)] bg-[var(--v2-page-bg)] px-4 text-sm font-medium text-[var(--v2-text-primary)]'
          id='forward-email'
          value={text}
          readOnly
          onCopy={() => {
            onCopy?.();
          }}
        />
        <Tooltip open={showCopied}>
          <TooltipTrigger asChild>
            <TooltipButton
              className={cn(
                'h-12 rounded-xl px-5 transition-all',
                showCopied
                  ? 'bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/15 dark:bg-emerald-500/15 dark:text-emerald-400'
                  : 'bg-[var(--v2-accent)]/10 text-[var(--v2-accent)] hover:bg-[var(--v2-accent)]/15 dark:bg-[var(--v2-accent)]/15 dark:text-[var(--v2-accent)]',
              )}
              onClick={async () => {
                onCopy?.();
                await navigator.clipboard.writeText(text);
                setShowCopied(true);
                await delayMs(1500);
                setShowCopied(false);
              }}
              size='sm'
              toolTipContent='Copy'
              toolTipSide='top'
            >
              {showCopied ? (
                <Check className='size-4' />
              ) : (
                <Copy className='size-4' />
              )}
            </TooltipButton>
          </TooltipTrigger>
          <TooltipContent>{m.dialog_copied()}</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}
